
	import { Component, Vue, Watch } from "vue-property-decorator";
	import { namespace, Getter } from "vuex-class";
	import NavHeader from "@/components/NavHeader.vue";
	import { get, cloneDeep } from "lodash";
	import { FeaturesList } from "@/store/types";
	import GenericTable, { TableHeader } from "@/components/table/generic-table.vue";
	import { CloseEventType, Tour } from "@/store/tours/types";
	import TourSetupModal from "@/components/TourSetupModal.vue";
	import api from "@/services/api.service";

	const Tours = namespace("tours");

	@Component({
		components: {
			"nav-header": NavHeader,
			"generic-table": GenericTable,
			"tour-modal": TourSetupModal
		}
	})
	export default class TourSetup extends Vue {
		/** Store Getter/Setter */
		@Getter getUserTenantGroupId: number;
		@Getter("getFeaturesList") featuresList: FeaturesList;

		@Tours.Action fetchAllTours: any;
		@Tours.Action removeTour: any;
		@Tours.Getter getTour: any;
		@Tours.Getter getAllTours: any;

		private isInvalid: boolean = true;
		private saveTourInProgress: boolean = false;
		private isLoading: boolean = false;
		private closeTourModal: boolean = false;
		private tourId: number = 0;

		// Modal display values
		private confirmModalVisible: boolean = false;
		private tourModalVisible: boolean = false;
		private detailsModalVisible: boolean = false;

		private currentTour: Tour = {
			groupId: 0,
			title: "",
			tourId: 0,
			tourOrder: 0,
			tourSteps: [],
			functionality: null,
			tourStepsCount: 0,
			matrixColumnSize: 3,
			matrixRowSize: 3
		};

		@Watch('tourModalVisible')
		private onTourModalVisibleChanged(val: boolean): void {
			if (!val) {
				this.cancel(CloseEventType.Default);
			}
		}

		private currentGroupTitle: string = "";

		private get disableSave(): boolean {
			return this.saveTourInProgress || this.isInvalid;
		}

		private get IsTourManagementEnabled(): boolean {
			return get(this.featuresList, ["Alarms", "MediaMatrix", "VideoTours"]);
		}

		private get tourGroupTitle(): string {
			return this.currentGroupTitle;
		}

		private get saveTourModal(): boolean {
			return this.saveTourInProgress;
		}

		private async mounted(): Promise<void> {
			this.isLoading = true;
			await this.getTours();
			this.tourId = 0;
		}

		private async getTours(): Promise<void> {
			this.isLoading = true;

			try {
				await this.fetchAllTours();
			} catch (error) {
				console.error("Unexpected exception in getting the tours", error);
			}

			this.isLoading = false;
		}

		private cloneTourTemplate(): Tour {
			const tourTemplate: any = {
				groupId: this.getUserTenantGroupId,
				title: "",
				tourId: 0,
				tourOrder: 0,
				tourSteps: [],
				functionality: null,
				tourStepsCount: 0,
				matrixColumnSize: 3,
				matrixRowSize: 3
			};
			// Deep cloned our template due to wanting it to be passed by VALUE not reference.
			return cloneDeep(tourTemplate);
		}

		private resetEditTour() : void {
			this.currentTour = this.cloneTourTemplate();
			this.isInvalid = true;
			this.tourId = 0;
			this.closeTourModal = false;
		}

		/** Cancel new tour modal */
		private async cancel(eventType: CloseEventType): Promise<void> {
			this.tourModalVisible = false;

			switch(eventType) {
				case CloseEventType.PostSave: {
					this.saveTourInProgress = false;
					await this.getTours();
					this.resetEditTour();
					break;
				}
				case CloseEventType.Default: {
					this.resetEditTour();
					break;
				}
				default: {
					// reset the value
					this.closeTourModal = false;
					break;
				}
			}
		}

		/** Save new or update tour */
		private async save() {
			this.saveTourInProgress = true;
		}

		private closeTour(): void {
			this.closeTourModal = true;
		}

		private editTourDetails(tourId: number) {
			this.tourModalVisible = true;
			this.tourId = tourId;
		}

		private modalRemoveTour(tourId: number): void {
			this.currentTour = this.getTour(tourId);
			this.confirmModalVisible = true;
		}

		private async showDetailsModal(tourId: number): Promise<void> {
			this.currentTour = this.getTour(tourId);

            // look up group title
			const tourGroup = await api.loadGroup(this.currentTour.groupId);
			this.currentGroupTitle = tourGroup.title;

			this.detailsModalVisible = true;
		}

		private removeTourFromList(): void {
			this.removeTour(this.currentTour.tourId);
			this.currentTour = this.cloneTourTemplate();
			this.confirmModalVisible = false;
		}

		private addTourSetup(): void {
			this.tourModalVisible = true;
			this.currentTour = this.cloneTourTemplate();
		}

		/** Generic table column headers */
		public columns: TableHeader[] = [
			{
				title: "Tour Title",
				key: "title",
				order: 1,
				sortOrder: 0,
				sortOrderReversed: false,
				description: "List of tours names",
				searchable: true,
				visible: true,
				dataType: "text",
				isTermLabel: true
			},
			{
				title: "Tour Steps",
				key: "tourStepsCount",
				order: 2,
				sortOrder: 0,
				sortOrderReversed: false,
				description: "Number of Tour steps",
				searchable: false,
				visible: true,
				dataType: "text",
				useCustomCell: true
			},
			{
				title: "",
				key: "functionality",
				order: 3,
				sortOrder: 0,
				sortOrderReversed: false,
				description: "Edit or Delete",
				searchable: false,
				visible: true,
				dataType: "text",
				useCustomCell: true
			}
		];

		private validateSetup(value: any) {
			this.isInvalid = value;
		}
	}
